import React, { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ThreeDots } from "react-loader-spinner";
import Datetime from "react-datetime";
import moment from "moment";
import "moment/locale/pt";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ErrorOutline as ErrorIcon,
} from "@material-ui/icons";

import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";
import CustomInput from "components/CustomInput/CustomInput";
import CustomReactSelect from "components/CustomDropdown/CustomReactSelect";
import {
  getSessions,
  createSession,
  updateSession,
  deleteSession,
  activateSession,
} from "services/governanceService";
import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle";
import useThemeColors from "hooks/useThemeColors";
import {
  grayColor,
  whiteColor,
  successColor,
  dangerColor,
} from "assets/jss/material-dashboard-pro-react";
import {
  defaultSessionPages,
  getPeriodOptions,
  getAvailablePeriodOptions,
  PERIOD_DEFAULT,
} from "./constants";

const useStyles = makeStyles((theme) => ({
  ...modalStyle(theme),
  modal: {
    ...modalStyle(theme).modal,
    [theme.breakpoints.up("sm")]: {
      minWidth: "600px",
      maxWidth: "800px",
      margin: "auto",
    },
    maxHeight: "85vh",
  },
  modalBody: {
    ...modalStyle(theme).modalBody,
    overflow: "visible",
  },
  modalFooter: {
    ...modalStyle(theme).modalFooter,
    padding: "15px 24px",
    borderTop: "1px solid " + grayColor[8],
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  },
  sessionList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    maxHeight: "320px",
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: "4px",
    "@supports (overflow: overlay)": {
      overflowY: "overlay",
    },
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&:hover": {
      scrollbarColor: grayColor[6] + " transparent",
    },
    "&::-webkit-scrollbar": {
      width: "6px",
      position: "absolute",
      right: 0,
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "transparent",
      borderRadius: "3px",
    },
    "&:hover::-webkit-scrollbar-thumb": {
      background: grayColor[6],
    },
  },
  sessionItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderRadius: "6px",
    border: "1px solid " + grayColor[8],
    backgroundColor: whiteColor,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: grayColor[10],
      borderColor: grayColor[6],
    },
  },
  sessionItemActive: {
    borderColor: successColor[0],
    backgroundColor: "rgba(76, 175, 80, 0.05)",
    "&:hover": {
      borderColor: successColor[0],
      backgroundColor: "rgba(76, 175, 80, 0.08)",
    },
  },
  sessionInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  sessionName: {
    fontSize: "15px",
    fontWeight: "500",
    color: grayColor[1],
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  sessionMeta: {
    fontSize: "12px",
    color: grayColor[3],
    display: "flex",
    gap: "12px",
  },
  sessionActions: {
    display: "flex",
    gap: "4px",
  },
  sessionActionButton: {
    padding: "6px",
    color: grayColor[3],
    "&:hover": {
      color: grayColor[1],
      backgroundColor: grayColor[10],
    },
  },
  sessionActionButtonDelete: {
    "&:hover": {
      color: dangerColor[0],
    },
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: grayColor[4],
    minHeight: "200px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyStateIcon: {
    fontSize: "48px",
    marginBottom: "16px",
    opacity: 0.5,
  },
  emptyStateText: {
    fontSize: "14px",
    marginBottom: "16px",
  },
  newSessionButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px",
    borderRadius: "6px",
    border: "none",
    backgroundColor: "transparent",
    color: grayColor[2],
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    marginTop: "12px",
    fontSize: "14px",
    fontWeight: "500",
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  formSection: {
    marginBottom: "20px",
  },
  formSectionTitle: {
    fontSize: "13px",
    fontWeight: "500",
    color: grayColor[2],
    textTransform: "uppercase",
    marginBottom: "4px",
    letterSpacing: "0.5px",
  },
  periodInlineRow: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
    "& > div": {
      flex: 1,
    },
  },
  datePickerWrapper: {
    flex: 1,
    "& .rdtPicker": {
      zIndex: 9999,
    },
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "8px 12px",
    borderRadius: "6px",
    backgroundColor: "transparent",
    color: grayColor[4],
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s ease",
    marginBottom: "16px",
    "&:hover": {
      backgroundColor: grayColor[10],
      color: grayColor[1],
    },
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
    gap: "16px",
    minHeight: "200px",
  },
  loadingText: {
    fontSize: "14px",
    color: grayColor[4],
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
    gap: "16px",
    color: dangerColor[0],
  },
  errorIcon: {
    fontSize: "48px",
    opacity: 0.8,
  },
  errorText: {
    fontSize: "14px",
    textAlign: "center",
  },
  retryButton: {
    marginTop: "8px",
  },
  formContainer: {
    position: "relative",
  },
  formOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    zIndex: 10,
    borderRadius: "4px",
    cursor: "not-allowed",
  },
}));

const availablePages = defaultSessionPages.map((p) => ({
  key: p.key,
  defaultOrder: p.order,
}));

// Map legacy period_type values (from old backend data) to new keys
const LEGACY_PERIOD_MAP = {
  ytd: "ytd_vs_ytd",
  last_12m: "last_12m_vs_prev",
  last_month: "last_month_vs_ly",
};

const normalizePeriodType = (raw) => {
  if (!raw) return PERIOD_DEFAULT;
  return LEGACY_PERIOD_MAP[raw] || raw;
};

const transformSessionFromApi = (apiSession) => {
  const pages = apiSession.pages
    ? apiSession.pages.map((p) => ({
        key: p.page_key,
        enabled: p.is_enabled,
        order: p.order,
      }))
    : availablePages.map((p) => ({
        key: p.key,
        enabled: true,
        order: p.defaultOrder,
      }));

  return {
    id: apiSession.id,
    name: apiSession.name,
    periodType: normalizePeriodType(apiSession.period_type),
    periodYear: apiSession.year,
    periodValue: apiSession.period_value || 1,
    isActive: apiSession.is_active,
    createdAt: apiSession.created_at
      ? new Date(apiSession.created_at).toLocaleDateString("pt-BR")
      : "",
    createdBy: apiSession.created_by_name || "Sistema",
    pages,
  };
};

const transformSessionToApi = (frontendSession) => {
  return {
    name: frontendSession.name,
    period_type: frontendSession.periodType || PERIOD_DEFAULT,
    year: frontendSession.periodYear,
    period_value: frontendSession.periodValue || 1,
  };
};

export default function SessionManagerModal({
  open,
  onClose,
  clientColor,
  preloadedSessions,
  sessionsLoading,
  onSessionsChanged,
  onConfirmSelection,
  pnlMetadata,
  availablePeriodsSet,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientTextColor } = useThemeColors();

  // Get last data month from pnlMetadata (fallback to current month if not available)
  const lastDataMonth = useMemo(() => {
    if (pnlMetadata?.pl?.latest_year && pnlMetadata?.pl?.latest_month) {
      return moment({
        year: pnlMetadata.pl.latest_year,
        month: pnlMetadata.pl.latest_month - 1, // moment months are 0-indexed
      });
    }
    // Fallback to current month
    return moment().startOf("month");
  }, [pnlMetadata]);

  const [sessions, setSessions] = useState([]);
  const [view, setView] = useState("list");
  const [editingSession, setEditingSession] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (preloadedSessions && preloadedSessions.length >= 0) {
      const transformedSessions = preloadedSessions.map(
        transformSessionFromApi,
      );
      setSessions(transformedSessions);
      const activeSession = transformedSessions.find((s) => s.isActive);
      if (activeSession) {
        setSelectedSessionId(activeSession.id);
      }
    }
  }, [preloadedSessions]);

  const [formName, setFormName] = useState("");
  const [formPeriodType, setFormPeriodType] = useState(PERIOD_DEFAULT);
  const [formPeriodDate, setFormPeriodDate] = useState(null);

  // Initialize formPeriodDate when lastDataMonth becomes available
  useEffect(() => {
    if (lastDataMonth && !formPeriodDate) {
      setFormPeriodDate(lastDataMonth.clone());
    }
  }, [lastDataMonth, formPeriodDate]);

  const [formPages, setFormPages] = useState(
    availablePages.map((p) => ({
      key: p.key,
      enabled: true,
      order: p.defaultOrder,
    })),
  );

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSessions();
      const transformedSessions = data.map(transformSessionFromApi);
      setSessions(transformedSessions);
      if (onSessionsChanged) {
        onSessionsChanged();
      }
    } catch (err) {
      console.error("Error loading sessions:", err);
      setError(
        t("alignment_session.session_manager.error_loading") ||
          "Erro ao carregar sessões. Tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  }, [t, onSessionsChanged]);

  useEffect(() => {
    if (open && !preloadedSessions && !sessionsLoading) {
      loadSessions();
    }
  }, [open, preloadedSessions, sessionsLoading, loadSessions]);

  const resetForm = useCallback(() => {
    setFormName("");
    setFormPeriodType(PERIOD_DEFAULT);
    setFormPeriodDate(lastDataMonth ? lastDataMonth.clone() : moment());
    setFormPages(
      availablePages.map((p) => ({
        key: p.key,
        enabled: true,
        order: p.defaultOrder,
      })),
    );
    setEditingSession(null);
  }, [lastDataMonth]);

  const handleNameBlur = useCallback(() => {
    setFormName((prev) => prev.trim().replace(/\s+/g, " "));
  }, []);

  const handleNewSession = useCallback(() => {
    resetForm();
    setView("form");
    setTimeout(() => {
      const nameInput = document.getElementById("session_name");
      if (nameInput) {
        nameInput.focus();
      }
    }, 100);
  }, [resetForm]);

  const handleEditSession = useCallback((session) => {
    setEditingSession(session);
    setFormName(session.name);
    setFormPeriodType(session.periodType);
    setFormPeriodDate(
      moment()
        .year(session.periodYear)
        .month(session.periodValue - 1),
    );
    setFormPages(session.pages);
    setView("form");
  }, []);

  const handleDeleteSession = useCallback(
    async (sessionId) => {
      if (
        !window.confirm(
          t("alignment_session.session_manager.confirm_delete") ||
            "Tem certeza que deseja excluir esta sessão?",
        )
      ) {
        return;
      }

      setSaving(true);
      setError(null);
      try {
        await deleteSession(sessionId);
        // Optimistic update for immediate feedback
        setSessions((prev) => {
          const remaining = prev.filter((s) => s.id !== sessionId);
          if (remaining.length > 0) {
            const mostRecent = remaining[0];
            setSelectedSessionId(mostRecent.id);
          } else {
            setSelectedSessionId(null);
          }
          return remaining;
        });
        // Sync with parent
        if (onSessionsChanged) await onSessionsChanged();
      } catch (err) {
        console.error("Error deleting session:", err);
        setError(
          t("alignment_session.session_manager.error_delete") ||
            "Erro ao excluir sessão. Tente novamente.",
        );
      } finally {
        setSaving(false);
      }
    },
    [t, onSessionsChanged],
  );

  const handleSelectSession = useCallback((sessionId) => {
    setSelectedSessionId(sessionId);
  }, []);

  const handleConfirmSelection = useCallback(async () => {
    if (!selectedSessionId) {
      onClose();
      return;
    }

    const currentActive = sessions.find((s) => s.isActive);
    if (currentActive?.id === selectedSessionId) {
      // Already active, just use from cache
      if (onConfirmSelection) onConfirmSelection(selectedSessionId);
      else onClose();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Persist activation in backend
      await activateSession(selectedSessionId);
      // Update local state
      setSessions((prev) =>
        prev.map((s) => ({
          ...s,
          isActive: s.id === selectedSessionId,
        })),
      );
      // Load session from cache (no API call)
      if (onConfirmSelection) {
        onConfirmSelection(selectedSessionId);
      } else {
        onClose();
      }
    } catch (err) {
      console.error("Error activating session:", err);
      setError(
        t("alignment_session.session_manager.error_activate") ||
          "Erro ao ativar sessão. Tente novamente.",
      );
    } finally {
      setSaving(false);
    }
  }, [selectedSessionId, sessions, onClose, t, onConfirmSelection]);

  const handleSaveSession = useCallback(async () => {
    if (!formName.trim()) return;

    setSaving(true);
    setError(null);

    const periodYear = moment.isMoment(formPeriodDate)
      ? formPeriodDate.year()
      : new Date().getFullYear();
    const periodValue = moment.isMoment(formPeriodDate)
      ? formPeriodDate.month() + 1
      : new Date().getMonth() + 1;

    const sessionData = {
      name: formName,
      periodType: formPeriodType,
      periodYear,
      periodValue,
      pages: formPages,
    };

    try {
      const apiData = transformSessionToApi(sessionData);

      let newSessionId = null;
      const editedId = editingSession?.id;

      if (editingSession) {
        await updateSession(editingSession.id, apiData);
      } else {
        const newSession = await createSession(apiData);
        if (newSession?.id) {
          newSessionId = newSession.id;
        }
      }

      // Single API call to refresh sessions - parent will update preloadedSessions
      if (onSessionsChanged) {
        await onSessionsChanged(editedId);
      }

      if (newSessionId) {
        setSelectedSessionId(newSessionId);
      }
      resetForm();
      setView("list");
    } catch (err) {
      console.error("Error saving session:", err);
      setError(
        t("alignment_session.session_manager.error_save") ||
          "Erro ao salvar sessão. Tente novamente.",
      );
    } finally {
      setSaving(false);
    }
  }, [
    formName,
    formPeriodType,
    formPeriodDate,
    formPages,
    editingSession,
    resetForm,
    t,
    onSessionsChanged,
  ]);

  const handleBackToList = useCallback(() => {
    resetForm();
    setView("list");
  }, [resetForm]);

  const handlePagesChange = useCallback((selectedOptions) => {
    const selectedKeys = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    setFormPages((prev) =>
      prev.map((p) => ({
        ...p,
        enabled: selectedKeys.includes(p.key),
      })),
    );
  }, []);

  const formatPeriodLabel = useCallback(
    (session) => {
      const { periodType, periodYear, periodValue } = session;
      const months = [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ];

      // Look up label from period options (same source as dropdown)
      const options = getPeriodOptions(t);
      const match = options.find((o) => o.key === periodType);
      const typeLabel = match?.label || periodType;
      return `${typeLabel} - ${months[periodValue - 1]}/${periodYear}`;
    },
    [t],
  );

  const countEnabledPages = useCallback((pages) => {
    return pages.filter((p) => p.enabled).length;
  }, []);

  const pageOptions = useMemo(
    () =>
      [...formPages]
        .sort((a, b) => a.order - b.order)
        .map((p) => ({
          value: p.key,
          label: t(`alignment_session.topics.${p.key}`) || p.key,
        })),
    [formPages, t],
  );

  const selectedPageOptions = useMemo(
    () =>
      formPages
        .filter((p) => p.enabled)
        .map((p) => ({
          value: p.key,
          label: t(`alignment_session.topics.${p.key}`) || p.key,
        })),
    [formPages, t],
  );

  const renderLoading = () => (
    <div className={classes.loadingContainer}>
      <ThreeDots
        height="40"
        width="40"
        radius="9"
        color={clientColor}
        ariaLabel="loading"
        visible={true}
      />
      <span className={classes.loadingText}>
        {t("alignment_session.session_manager.loading") ||
          "carregando sessões..."}
      </span>
    </div>
  );

  const renderError = () => (
    <div className={classes.errorContainer}>
      <ErrorIcon className={classes.errorIcon} />
      <span className={classes.errorText}>{error}</span>
      <Button
        className={classes.retryButton}
        style={{ backgroundColor: clientColor, color: clientTextColor }}
        onClick={loadSessions}
      >
        {t("common.retry") || "Tentar novamente"}
      </Button>
    </div>
  );

  const renderListView = () => {
    if (loading || sessionsLoading) {
      return renderLoading();
    }

    if (error && sessions.length === 0) {
      return renderError();
    }

    return (
      <>
        {error && (
          <div
            style={{
              backgroundColor: "rgba(244, 67, 54, 0.1)",
              color: dangerColor[0],
              padding: "12px 16px",
              borderRadius: "6px",
              marginBottom: "16px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}
        <div className={classes.sessionList}>
          {sessions.length === 0 ? (
            <div className={classes.emptyState}>
              <AddIcon className={classes.emptyStateIcon} />
              <p className={classes.emptyStateText}>
                {t("alignment_session.session_manager.no_sessions") ||
                  "Nenhuma sessão criada ainda"}
              </p>
              <Button
                style={{ backgroundColor: clientColor, color: clientTextColor }}
                onClick={handleNewSession}
              >
                {t("alignment_session.session_manager.create_first") ||
                  "Criar primeira sessão"}
              </Button>
            </div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.id}
                className={`${classes.sessionItem} ${selectedSessionId === session.id ? classes.sessionItemActive : ""}`}
                onClick={() => !saving && handleSelectSession(session.id)}
                style={{
                  cursor: saving ? "default" : "pointer",
                  opacity: saving ? 0.7 : 1,
                }}
                role="button"
                tabIndex={saving ? -1 : 0}
                onKeyDown={(e) => {
                  if (!saving && (e.key === "Enter" || e.key === " ")) {
                    handleSelectSession(session.id);
                  }
                }}
              >
                <div className={classes.sessionInfo}>
                  <div className={classes.sessionName}>
                    {session.name}
                    {session.isActive && (
                      <span
                        style={{
                          fontSize: "12px",
                          lineHeight: "1.05",
                          fontWeight: 300,
                          // textTransform: "uppercase",
                          color: clientTextColor,
                          backgroundColor: clientColor,
                          padding: "2px 8px",
                          borderRadius: "8px",
                          marginBottom: "3px",
                        }}
                      >
                        {t("alignment_session.session_manager.current_session")}
                      </span>
                    )}
                  </div>
                  <div className={classes.sessionMeta}>
                    <span>
                      {t("alignment_session.session_manager.period") ||
                        "Período"}
                      : {formatPeriodLabel(session)}
                    </span>
                    <span>
                      {t("alignment_session.session_manager.pages") ||
                        "Páginas"}
                      : {countEnabledPages(session.pages)}/
                      {session.pages.length}
                    </span>
                    <span>
                      {t("alignment_session.session_manager.created") ||
                        "Criado"}
                      : {session.createdAt}
                    </span>
                  </div>
                </div>
                <div
                  className={classes.sessionActions}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip
                    title={
                      t("alignment_session.session_manager.edit") || "Editar"
                    }
                  >
                    <IconButton
                      size="small"
                      className={classes.sessionActionButton}
                      onClick={() => handleEditSession(session)}
                      disabled={saving}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      t("alignment_session.session_manager.delete") || "Excluir"
                    }
                  >
                    <IconButton
                      size="small"
                      className={`${classes.sessionActionButton} ${classes.sessionActionButtonDelete}`}
                      onClick={() => handleDeleteSession(session.id)}
                      disabled={saving}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            ))
          )}
        </div>

        {sessions.length > 0 && (
          <button
            className={classes.newSessionButton}
            onClick={handleNewSession}
            disabled={saving}
          >
            <AddIcon />
            {t("alignment_session.session_manager.new_session") ||
              "Nova sessão"}
          </button>
        )}
      </>
    );
  };

  // Build period options with availability based on the selected formPeriodDate
  const periodReferenceOptions = useMemo(() => {
    const refYear = moment.isMoment(formPeriodDate)
      ? formPeriodDate.year()
      : null;
    const refMonth = moment.isMoment(formPeriodDate)
      ? formPeriodDate.month() + 1
      : null;

    return getAvailablePeriodOptions(
      availablePeriodsSet,
      refYear,
      refMonth,
      t,
    ).map((opt) => ({
      value: opt.key,
      label: opt.label,
      isDisabled: opt.isDisabled,
    }));
  }, [availablePeriodsSet, formPeriodDate, t]);

  // Auto-correct formPeriodType when the selected option becomes unavailable
  useEffect(() => {
    if (periodReferenceOptions.length === 0) return;
    const currentOption = periodReferenceOptions.find(
      (opt) => opt.value === formPeriodType,
    );
    if (currentOption && currentOption.isDisabled) {
      const firstAvailable = periodReferenceOptions.find(
        (opt) => !opt.isDisabled,
      );
      if (firstAvailable) {
        setFormPeriodType(firstAvailable.value);
      }
    }
  }, [periodReferenceOptions, formPeriodType]);

  const isValidDate = useCallback(
    (currentDate) => {
      const twoYearsAgo = moment().subtract(2, "year").startOf("month");
      const maxDate = lastDataMonth
        ? lastDataMonth.clone().endOf("month")
        : moment().endOf("month");
      return (
        currentDate.isAfter(twoYearsAgo) && currentDate.isSameOrBefore(maxDate)
      );
    },
    [lastDataMonth],
  );

  const renderDateInput = useCallback(
    // eslint-disable-next-line react/prop-types
    ({ value, ...restProps }, openCalendar) => (
      <CustomInput
        formControlProps={{
          fullWidth: true,
          style: { paddingTop: "0", marginTop: "-6px" },
        }}
        inputProps={{
          type: "text",
          autoComplete: "off",
          readOnly: true,
          onClick: openCalendar,
          style: { cursor: "pointer" },
          ...restProps,
        }}
        value={value}
        client
      />
    ),
    [],
  );

  const renderFormView = () => (
    <>
      <button
        className={classes.backButton}
        onClick={handleBackToList}
        disabled={saving}
      >
        <ArrowBackIcon fontSize="small" />
        {t("alignment_session.session_manager.back_to_list") ||
          "Voltar para lista"}
      </button>

      <div className={classes.formContainer}>
        {saving && <div className={classes.formOverlay} />}

        <div className={classes.formSection}>
          <div className={classes.formSectionTitle}>
            {t("alignment_session.session_manager.basic_info") ||
              "Informações básicas"}
          </div>
          <CustomInput
            id="session_name"
            labelText={
              t("alignment_session.session_manager.session_name") ||
              "Nome da sessão"
            }
            value={formName}
            onChange={(e) => {
              if (saving) return;
              const value = e.target.value;
              if (value.length <= 40) {
                setFormName(value);
              }
            }}
            onBlur={handleNameBlur}
            formControlProps={{
              fullWidth: true,
              style: { marginTop: "-10px" },
            }}
            inputProps={{
              maxLength: 40,
              autoComplete: "off",
            }}
            client
          />
        </div>

        <div className={classes.formSection}>
          <div className={classes.formSectionTitle}>
            {t("alignment_session.session_manager.period_reference") ||
              "Período de referência"}
          </div>
          <div className={classes.periodInlineRow}>
            <CustomReactSelect
              title={
                t("alignment_session.session_manager.reference_type") ||
                "Tipo de referência"
              }
              isClearable={false}
              isSearchable={false}
              closeMenuOnSelect={true}
              options={periodReferenceOptions}
              value={periodReferenceOptions.find(
                (opt) => opt.value === formPeriodType,
              )}
              onChange={(selected) => {
                if (saving) return;
                setFormPeriodType(selected?.value || PERIOD_DEFAULT);
              }}
              isOptionDisabled={(opt) => !!opt.isDisabled}
              baseColor={clientColor}
              optionVerticalPadding="4px"
              style={{ marginTop: "0px" }}
            />
            <div className={classes.datePickerWrapper}>
              <label
                style={{
                  fontSize: "14px",
                  color: grayColor[3],
                  fontWeight: "300",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                {t("alignment_session.session_manager.year_month") || "Ano/Mês"}{" "}
                <span style={{ fontSize: "12px", fontStyle: "italic" }}>
                  (
                  {t("alignment_session.session_manager.last_data_month") ||
                    "último mês de dados"}
                  )
                </span>
              </label>
              <Datetime
                value={formPeriodDate}
                timeFormat={false}
                closeOnSelect={true}
                dateFormat="YYYY-MM"
                locale="pt"
                renderInput={renderDateInput}
                onChange={(date) => {
                  if (saving) return;
                  if (moment.isMoment(date)) {
                    setFormPeriodDate(date);
                  }
                }}
                isValidDate={isValidDate}
              />
            </div>
          </div>
        </div>

        <div className={classes.formSection}>
          <div className={classes.formSectionTitle}>
            {t("alignment_session.session_manager.enabled_pages") ||
              "Páginas habilitadas"}
          </div>
          <CustomReactSelect
            title={
              t("alignment_session.session_manager.select_pages") ||
              "Selecione as páginas"
            }
            isMulti
            isClearable={false}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            options={pageOptions}
            value={selectedPageOptions}
            onChange={(newValue) => {
              if (saving) return;
              handlePagesChange(newValue);
            }}
            baseColor={clientColor}
            optionVerticalPadding="4px"
            style={{ marginTop: "0px" }}
          />
        </div>
      </div>
    </>
  );

  return (
    <Dialog
      classes={{
        root: classes.modalRoot,
        paper: classes.modal,
      }}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <ModalHeader
        title={
          view === "list"
            ? t("alignment_session.session_manager.title") ||
              "Gerenciar Sessões"
            : editingSession
              ? t("alignment_session.session_manager.edit_session") ||
                "Editar Sessão"
              : t("alignment_session.session_manager.new_session") ||
                "Nova Sessão"
        }
        onClose={onClose}
        closeDisabled={saving}
        closeLabel={t("common.close")}
        clientColor={clientColor}
      />

      <DialogContent className={classes.modalBody}>
        {view === "list" ? renderListView() : renderFormView()}
      </DialogContent>

      {view === "list" && sessions.length > 0 && (
        <DialogActions
          className={classes.modalFooter}
          style={{ justifyContent: "space-between" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              minWidth: "40px",
              marginLeft: "8px",
            }}
          >
            {saving && (
              <ThreeDots
                height="20"
                width="30"
                radius="5"
                color={clientColor}
                ariaLabel="loading"
                visible={true}
              />
            )}
          </div>
          <Button
            style={{ backgroundColor: clientColor, color: clientTextColor }}
            onClick={handleConfirmSelection}
            disabled={
              saving ||
              !selectedSessionId ||
              sessions.find((s) => s.isActive)?.id === selectedSessionId
            }
          >
            {t("alignment_session.session_manager.load")}
          </Button>
        </DialogActions>
      )}

      {view === "form" && (
        <DialogActions className={classes.modalFooter}>
          <Button
            color="transparent"
            onClick={handleBackToList}
            disabled={saving}
          >
            {t("common.cancel") || "Cancelar"}
          </Button>
          <Button
            style={{ backgroundColor: clientColor, color: clientTextColor }}
            onClick={handleSaveSession}
            disabled={
              !formName.trim() || saving || selectedPageOptions.length === 0
            }
          >
            {saving ? (
              <ThreeDots
                height="20"
                width="30"
                radius="5"
                color={whiteColor}
                ariaLabel="saving"
                visible={true}
              />
            ) : editingSession ? (
              t("common.save") || "Salvar"
            ) : (
              t("common.create") || "Criar"
            )}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

SessionManagerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientColor: PropTypes.string,
  preloadedSessions: PropTypes.array,
  sessionsLoading: PropTypes.bool,
  onSessionsChanged: PropTypes.func,
  onConfirmSelection: PropTypes.func,
  pnlMetadata: PropTypes.object,
  availablePeriodsSet: PropTypes.instanceOf(Set),
};

SessionManagerModal.defaultProps = {
  clientColor: "#4caf50",
  preloadedSessions: null,
  sessionsLoading: false,
  onSessionsChanged: null,
  onConfirmSelection: null,
  pnlMetadata: null,
  availablePeriodsSet: null,
};
